import { useState, useEffect } from "react";
import { format } from "date-fns";
import { Lock, Flashlight, Camera } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

interface LockScreenProps {
  onUnlock: () => void;
}

export function LockScreen({ onUnlock }: LockScreenProps) {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [isUnlocking, setIsUnlocking] = useState(false);
  const [swipeY, setSwipeY] = useState(0);

  // Update time every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const handleUnlock = () => {
    setIsUnlocking(true);
    setTimeout(() => {
      onUnlock();
    }, 300);
  };

  const handleTouchStart = (e: React.TouchEvent | React.MouseEvent) => {
    const clientY =
      "touches" in e ? e.touches[0].clientY : e.clientY;
    setSwipeY(clientY);
  };

  const handleTouchMove = (e: React.TouchEvent | React.MouseEvent) => {
    const clientY =
      "touches" in e ? e.touches[0].clientY : e.clientY;
    const delta = swipeY - clientY;
    if (delta > 100) {
      handleUnlock();
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0, y: -100 }}
      className="absolute inset-0 z-50 flex flex-col bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900"
      onClick={handleUnlock}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
    >
      {/* Status Bar */}
      <div className="flex items-center justify-between px-6 pt-3 text-white">
        <span className="text-sm font-semibold">
          {format(currentTime, "h:mm a")}
        </span>
        <div className="flex items-center gap-1">
          <div className="h-4 w-4 rounded-full border-2 border-white" />
          <div className="h-4 w-4 rounded-full border-2 border-white" />
          <div className="h-2 w-6 rounded-sm border-2 border-white bg-white" />
        </div>
      </div>

      {/* Main Content */}
      <div className="flex flex-1 flex-col items-center justify-start pt-20">
        {/* Lock Icon */}
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="mb-6 rounded-full bg-white/20 p-3 backdrop-blur-md"
        >
          <Lock className="h-6 w-6 text-white" />
        </motion.div>

        {/* Time */}
        <motion.h1
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="text-8xl font-light tracking-tight text-white"
          style={{ fontFamily: "-apple-system, SF Pro Display" }}
        >
          {format(currentTime, "h:mm")}
        </motion.h1>

        {/* Date */}
        <motion.p
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="mt-2 text-2xl font-normal text-white/90"
        >
          {format(currentTime, "EEEE, MMMM d")}
        </motion.p>

        {/* Memorial Notice */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="mt-8 max-w-xs rounded-2xl bg-white/10 p-4 text-center backdrop-blur-md"
        >
          <p className="text-sm text-white/80">
            In loving memory
          </p>
          <p className="mt-1 text-lg font-semibold text-white">
            Dad's iPhone Memorial
          </p>
          <p className="mt-2 text-xs text-white/60">
            Click anywhere to access memories
          </p>
        </motion.div>
      </div>

      {/* Bottom Actions */}
      <div className="flex items-center justify-between px-12 pb-12">
        {/* Flashlight */}
        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
          className="flex h-12 w-12 items-center justify-center rounded-full bg-white/20 backdrop-blur-md"
        >
          <Flashlight className="h-5 w-5 text-white" />
        </motion.button>

        {/* Swipe Up Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="flex flex-col items-center"
        >
          <motion.div
            animate={{ y: [0, -8, 0] }}
            transition={{ repeat: Infinity, duration: 1.5 }}
            className="mb-2 h-1 w-16 rounded-full bg-white/50"
          />
          <span className="text-xs text-white/60">Swipe up</span>
        </motion.div>

        {/* Camera */}
        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
          className="flex h-12 w-12 items-center justify-center rounded-full bg-white/20 backdrop-blur-md"
        >
          <Camera className="h-5 w-5 text-white" />
        </motion.button>
      </div>

      {/* Home Indicator */}
      <div className="absolute bottom-2 left-1/2 h-1 w-32 -translate-x-1/2 rounded-full bg-white" />
    </motion.div>
  );
}